import path from 'node:path';
import type {
  Middleware,
  RouteMiddleware,
  GlobalMiddleware,
  MiddlewareContext,
  ExpressApp,
  ViteMiddleware,
} from './types';

/**
 * Enhance Request/Response objects with Express-compatible methods
 * This allows middleware written for Express to work with Vite/Connect
 */
function enhanceForCompat(req: any, res: any): void {
  // Enhance Response with Express-like methods
  if (!res.status) {
    res.status = function (code: number) {
      res.statusCode = code;
      return res;
    };
  }

  if (!res.json) {
    res.json = function (data: unknown) {
      res.setHeader('Content-Type', 'application/json');
      res.end(JSON.stringify(data));
      return res;
    };
  }

  if (!res.send) {
    res.send = function (data: unknown) {
      if (typeof data === 'object' && data !== null) {
        res.setHeader('Content-Type', 'application/json');
        res.end(JSON.stringify(data));
      } else {
        res.end(String(data));
      }
      return res;
    };
  }

  // Enhance Request with parsed query
  if (!req.query) {
    const url = new URL(req.url || '', `http://${req.headers.host || 'localhost'}`);
    req.query = Object.fromEntries(url.searchParams.entries());
  }
}

/**
 * Detect if the server is a Connect/Vite middleware server (not Express)
 * Connect servers don't have Express-specific methods like 'set' or 'engine'
 */
function isConnectServer(server: unknown): boolean {
  return (
    typeof server === 'function' &&
    !('set' in (server as object)) &&
    !('engine' in (server as object))
  );
}
/**
 * Type guard to check if middleware is route-based
 */
function isRouteMiddleware(middleware: Middleware): middleware is RouteMiddleware {
  return 'createRouter' in middleware && middleware.createRouter !== undefined;
}

/**
 * Type guard to check if middleware is global
 */
function isGlobalMiddleware(middleware: Middleware): middleware is GlobalMiddleware {
  return 'createHandler' in middleware && middleware.createHandler !== undefined;
}

/**
 * Compute the full mount path by combining basePath and middleware mountPath
 */
function computeMountPath(basePath: string, mountPath: string): string {
  const routePath = path.posix.join(basePath, mountPath);
  return routePath.startsWith('/') ? routePath : `/${routePath}`;
}

/**
 * Log middleware registration with routes info
 */
function logMiddlewareRegistration(
  middleware: RouteMiddleware,
  fullMountPath: string,
): void {
  if (middleware.routes && middleware.routes.length > 0) {
    console.log(`[Middleware] Registered: ${middleware.name} at ${fullMountPath}`);
    middleware.routes.forEach((route) => {
      const routePath = route.path === '/' ? fullMountPath : path.posix.join(fullMountPath, route.path);
      console.log(`  ${route.method} ${routePath} - ${route.description}`);
    });
  } else {
    console.log(`[Middleware] Registered: ${middleware.name} at ${fullMountPath}`);
  }
}

// MUST stay synchronous. See note on `registerMiddlewares` below.
function registerRouteMiddleware(
  server: ExpressApp | ViteMiddleware,
  middleware: RouteMiddleware,
  context: MiddlewareContext,
): void {
  if (!middleware.mountPath) {
    console.error(
      `[Middleware] ${middleware.name}: Route middleware must have mountPath. Skipping.`
    );
    return;
  }

  const router = middleware.createRouter(context);
  const fullMountPath = computeMountPath(context.basePath, middleware.mountPath);

  server.use(fullMountPath, router);
  logMiddlewareRegistration(middleware, fullMountPath);
}

// MUST stay synchronous. See note on `registerMiddlewares` below.
function registerGlobalMiddleware(
  server: ExpressApp | ViteMiddleware,
  middleware: GlobalMiddleware,
  context: MiddlewareContext,
): void {
  if (middleware.mountPath) {
    console.warn(
      `[Middleware] ${middleware.name}: Global middleware should not have mountPath. ` +
      `Ignoring mountPath "${middleware.mountPath}".`
    );
  }

  const handler = middleware.createHandler(context);
  server.use(handler);
  console.log(`[Middleware] Registered global: ${middleware.name}`);
}

/**
 * Register middlewares for Express-compatible servers or Vite.
 *
 * MUST stay synchronous (no async/await).
 *
 * Why: when called from webpack/rspack-dev-server's `setupMiddlewares` callback,
 * any `await` here yields a microtask, allowing dev-server to finish registering
 * its own built-ins (proxy, historyApiFallback, serveStatic, ...) BEFORE our
 * later iterations run. The first router lands before historyApiFallback while
 * subsequent routers land after — causing extensionless GETs (e.g. `/dev/openapi/spec`
 * with a wildcard Accept header) to be rewritten to `/index.html`. All registered work
 * (`server.use`, `createRouter`, `createHandler`) is sync, so async/await is also
 * unnecessary.
 *
 * @example
 * ```typescript
 * // In rspack/webpack setupMiddlewares
 * setupMiddlewares: (middlewares, devServer) => {
 *   if (devServer.app) {
 *     registerMiddlewares(devServer.app, [
 *       createDevLogsMiddleware({ logDir: './logs' }),
 *       createOpenapiMiddleware({ openapiFilePath: './openapi.json' })
 *     ], { basePath: '/api', isDev: true, rootDir: __dirname });
 *   }
 *   return middlewares;
 * }
 * ```
 */
export function registerMiddlewares(
  server: ExpressApp | ViteMiddleware,
  middlewares: Middleware[],
  options?: Partial<MiddlewareContext>,
): void {
  // Build context with defaults
  const context: MiddlewareContext = {
    basePath: '/',
    isDev: process.env.NODE_ENV !== 'production',
    rootDir: process.cwd(),
    ...options,
  };

  // If running in Connect/Vite environment, register compatibility middleware first
  // This adds Express-like methods (res.status, res.json, req.query) to the request/response
  if (isConnectServer(server)) {
    server.use((req: any, res: any, next: any) => {
      enhanceForCompat(req, res);
      next();
    });
    console.log('[Middleware] Registered Express compatibility layer for Connect/Vite');
  }

  // 合并默认中间件和用户提供的中间件
  const allMiddlewares = [...middlewares];

  for (const middleware of allMiddlewares) {
    // Check if middleware should be enabled
    if (middleware.enabled && !middleware.enabled(context)) {
      continue;
    }

    try {
      // Check if both createRouter and createHandler are provided (type-unsafe case)
      const hasCreateRouter = 'createRouter' in middleware && typeof (middleware as any).createRouter === 'function';
      const hasCreateHandler = 'createHandler' in middleware && typeof (middleware as any).createHandler === 'function';

      if (hasCreateRouter && hasCreateHandler) {
        console.warn(
          `[Middleware] ${middleware.name}: Both createRouter and createHandler provided. ` +
          `Using createRouter and ignoring createHandler.`
        );
      }

      if (isRouteMiddleware(middleware)) {
        registerRouteMiddleware(server, middleware, context);
      } else if (isGlobalMiddleware(middleware)) {
        registerGlobalMiddleware(server, middleware, context);
      } else {
        // Type-unsafe middleware that has neither createRouter nor createHandler
        console.error(
          `[Middleware] ${(middleware as any).name || 'unknown'}: Must provide either createRouter or createHandler.`
        );
      }
    } catch (error) {
      console.error(`[Middleware] Failed to register ${middleware.name}:`, error);
    }
  }
}

// Re-export types and middleware creators
export type {
  Middleware,
  RouteMiddleware,
  GlobalMiddleware,
  MiddlewareContext,
  RouteInfo,
} from './types';
export { createOpenapiMiddleware, parseApiRoutes } from './openapi';
export { createDevLogsMiddleware } from './dev-logs';
export { createCollectLogsMiddleware } from './collect-logs';
// Export compat utilities for controllers that want to explicitly use them
export { sendJson, sendError, sendSuccess, getQuery, getQueryParam } from './compat';
